import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  getClass,
  updateClass,
  getStudents,
  getAttendanceGrid,
  getAssignments,
  getSubmissions,
  getStudentAnswers,
  createStudent,
  updateStudent,
  addStudentToClass,
  removeStudentFromClass,
  createSession,
  updateCell,
  updateSession,
  generateLessons,
  deleteSession,
  createAssignment,
  deleteAssignment,
  createSubmission,
  getCurriculum,
  replaceCurriculum,
  scanTopics,
  CurriculumItem,
  ClassOut,
  Student,
  AttendanceGrid,
  GridColumn,
  Assignment,
  Submission,
} from "../api/client";

const STATUSES = ["IP", "CU", "X", ""] as const;

function cleanTitle(title: string): string {
  return title.replace(/^\[Intro\]\s*/, "").replace(/^\[Intermediate\]\s*/, "").replace(/^\[Advanced\]\s*/, "");
}
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  IP: { bg: "var(--green-light)", color: "var(--green-text)" },
  CU: { bg: "var(--amber-light)", color: "var(--amber-text)" },
  X: { bg: "var(--red-light)", color: "var(--red-text)" },
  "": { bg: "transparent", color: "var(--text-muted)" },
};

function groupByMonth(columns: GridColumn[]): { month: string; cols: GridColumn[] }[] {
  const groups: { month: string; cols: GridColumn[] }[] = [];
  let current: { month: string; cols: GridColumn[] } | null = null;
  for (const col of columns) {
    if (!current || current.month !== col.month) {
      current = { month: col.month, cols: [] };
      groups.push(current);
    }
    current.cols.push(col);
  }
  return groups;
}

function ViewAssignmentModal({ assignment, classId, students, onClose }: {
  assignment: any;
  classId: string;
  students: { id: string; name: string }[];
  onClose: () => void;
}) {
  const [allAnswers, setAllAnswers] = useState<Record<string, Record<string, { answer_text: string; submitted_at: string }>>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      const result: typeof allAnswers = {};
      for (const s of students) {
        try {
          const r = await getStudentAnswers(classId, s.id);
          result[s.id] = r.data;
        } catch { result[s.id] = {}; }
      }
      setAllAnswers(result);
      setLoading(false);
    };
    load();
  }, [classId, students]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 700, maxHeight: "90vh", overflow: "auto" }}>
        <h3>{assignment.title}</h3>
        {assignment.description && <p style={{ color: "var(--text-secondary)", marginBottom: 12 }}>{assignment.description}</p>}
        {assignment.due_date && <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 16 }}>Due: {assignment.due_date}</p>}

        {loading ? (
          <div style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Loading student answers...</div>
        ) : (
          assignment.questions?.map((q: any, i: number) => (
            <div key={q.id} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8, padding: "8px 12px", background: "var(--bg)", borderRadius: 8 }}>
                Q{i + 1}. {q.question_text}
                {q.answer_text && (
                  <div style={{ fontWeight: 400, fontSize: 12, color: "var(--accent-text)", marginTop: 4 }}>
                    Expected: {q.answer_text}
                  </div>
                )}
              </div>
              <div style={{ paddingLeft: 12 }}>
                {students.map((s) => {
                  const ans = allAnswers[s.id]?.[q.id];
                  return (
                    <div key={s.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                      <span style={{ fontWeight: 500, minWidth: 100, color: "var(--text-primary)" }}>{s.name}</span>
                      {ans ? (
                        <span style={{ flex: 1 }}>
                          <span>{ans.answer_text}</span>
                          <span style={{ fontSize: 10, color: "var(--text-muted)", marginLeft: 8 }}>
                            {new Date(ans.submitted_at).toLocaleDateString()}
                          </span>
                        </span>
                      ) : (
                        <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>Not submitted</span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default function ClassDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [notFound, setNotFound] = useState(false);
  const [cls, setCls] = useState<ClassOut | null>(null);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [grid, setGrid] = useState<AttendanceGrid | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [curriculum, setCurriculum] = useState<CurriculumItem[]>([]);
  const [tab, setTab] = useState<"students" | "attendance" | "assignments" | "settings">("students");
  const [telegramForm, setTelegramForm] = useState({ chat_id: "", hw_topic_id: "", reminder_topic_id: "" });
  const [telegramSaved, setTelegramSaved] = useState(false);

  // Modals
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [showCreateStudent, setShowCreateStudent] = useState(false);
  const [newStudentForm, setNewStudentForm] = useState({ name: "", phone: "", email: "" });
  const [showAddLesson, setShowAddLesson] = useState(false);
  const [newDate, setNewDate] = useState(new Date().toISOString().split("T")[0]);
  const [newTopic, setNewTopic] = useState("");
  const [newLessonNum, setNewLessonNum] = useState("");
  const [addLessonCurrId, setAddLessonCurrId] = useState("");  // curriculum item id or "__special__"
  const [editLessonCurrId, setEditLessonCurrId] = useState("");
  const [showGenerate, setShowGenerate] = useState(false);
  const [genStart, setGenStart] = useState(new Date().toISOString().split("T")[0]);
  const [genEnd, setGenEnd] = useState("");
  const [genStartNum, setGenStartNum] = useState("1");
  const [genResult, setGenResult] = useState("");
  const [editLesson, setEditLesson] = useState<GridColumn | null>(null);
  const [editForm, setEditForm] = useState({ date: "", topic: "", lesson_number: "" });
  const [editLessonMode, setEditLessonMode] = useState<"curriculum" | "special" | "custom">("curriculum");
  const [showAddAssignment, setShowAddAssignment] = useState(false);
  const [assignForm, setAssignForm] = useState({ title: "", description: "", due_date: "" });
  const [assignQuestions, setAssignQuestions] = useState<{ question_text: string; answer_text: string }[]>([{ question_text: "", answer_text: "" }]);
  const [showSubmitModal, setShowSubmitModal] = useState<string | null>(null);
  const [submitForm, setSubmitForm] = useState({ student_id: "", text_content: "", files: null as FileList | null });
  const [viewAssignment, setViewAssignment] = useState<any>(null);

  const loadClass = useCallback(() => {
    if (!id) return;
    getClass(id).then((r) => {
      setCls(r.data);
      setTelegramForm({
        chat_id: r.data.telegram_chat_id || "",
        hw_topic_id: r.data.telegram_hw_topic_id || "",
        reminder_topic_id: r.data.telegram_reminder_topic_id || "",
      });
    }).catch(() => setNotFound(true));
    getStudents().then((r) => setAllStudents(r.data));
  }, [id]);

  const loadGrid = useCallback(() => {
    if (!id) return;
    getAttendanceGrid(id).then((r) => setGrid(r.data)).catch(() => {});
  }, [id]);

  const loadAssignments = useCallback(() => {
    if (!id) return;
    getAssignments(id).then((r) => setAssignments(r.data)).catch(() => {});
    getSubmissions(undefined, undefined).then((r) => setSubmissions(r.data)).catch(() => {});
  }, [id]);

  const loadCurriculum = useCallback(async () => {
    if (!id) return;
    try {
      const r = await getCurriculum(id);
      if (r.data.length === 0) {
        // Auto-load full curriculum if empty
        const { FULL_CURRICULUM } = await import("../data/lessonPresets");
        await replaceCurriculum(id, FULL_CURRICULUM);
        const r2 = await getCurriculum(id);
        setCurriculum(r2.data);
      } else {
        setCurriculum(r.data);
      }
    } catch {}
  }, [id]);

  useEffect(() => { loadClass(); loadGrid(); loadAssignments(); loadCurriculum(); }, [loadClass, loadGrid, loadAssignments, loadCurriculum]);

  // --- Students ---
  const enrolledIds = new Set(cls?.students?.map((s) => s.id) || []);
  const availableStudents = allStudents.filter((s) => !enrolledIds.has(s.id));

  const handleAddStudent = async () => {
    if (!id || !selectedStudentId) return;
    await addStudentToClass(id, selectedStudentId);
    setSelectedStudentId("");
    setShowAddStudent(false);
    loadClass();
    loadGrid();
  };

  const handleCreateAndAddStudent = async () => {
    if (!id || !newStudentForm.name.trim()) return;
    const r = await createStudent(newStudentForm);
    await addStudentToClass(id, r.data.id);
    setNewStudentForm({ name: "", phone: "", email: "" });
    setShowCreateStudent(false);
    setShowAddStudent(false);
    loadClass();
    loadGrid();
  };

  const handleRemoveStudent = async (studentId: string, name: string) => {
    if (!id || !window.confirm(`Remove ${name} from this class?`)) return;
    await removeStudentFromClass(id, studentId);
    loadClass();
    loadGrid();
  };

  // --- Attendance ---
  const handleCellClick = async (sessionId: string, studentId: string, currentStatus: string) => {
    const idx = STATUSES.indexOf(currentStatus as any);
    const next = STATUSES[(idx + 1) % STATUSES.length];
    setGrid((prev) => {
      if (!prev) return prev;
      const newGrid = { ...prev, grid: { ...prev.grid } };
      newGrid.grid[studentId] = { ...newGrid.grid[studentId], [sessionId]: next };
      return newGrid;
    });
    await updateCell({ session_id: sessionId, student_id: studentId, status: next });
  };

  const handleAddLesson = async () => {
    if (!id || !newDate) return;
    let topic = newTopic || undefined;
    let lessonNum = newLessonNum ? parseInt(newLessonNum) : undefined;
    if (addLessonCurrId === "__special__" && newTopic) {
      topic = `[Special Lesson] ${newTopic}`;
      lessonNum = 0;
    }
    try {
      await createSession({
        class_id: id,
        date: newDate,
        lesson_number: lessonNum,
        topic,
        attendances: [],
      });
      setShowAddLesson(false);
      setNewTopic("");
      setNewLessonNum("");
      setAddLessonCurrId("");
      loadGrid();
    } catch (e: any) {
      alert("Failed to add lesson: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleDeleteLesson = async (sessionId: string, date: string) => {
    if (!window.confirm(`Delete lesson on ${date}?`)) return;
    await deleteSession(sessionId);
    loadGrid();
  };

  const handleGenerate = async () => {
    if (!id || !genStart || !genEnd) return;
    try {
      const r = await generateLessons({
        class_id: id,
        start_date: genStart,
        end_date: genEnd,
        start_lesson_number: parseInt(genStartNum) || 1,
      });
      setGenResult(`Created ${r.data.created} lessons (${r.data.parsed_days.join(", ")})`);
      loadGrid();
    } catch (e: any) {
      setGenResult(e.response?.data?.detail || "Error generating lessons");
    }
  };

  const handleEditLesson = async () => {
    if (!editLesson) return;
    let topic = editForm.topic || undefined;
    let lessonNum = editForm.lesson_number ? parseInt(editForm.lesson_number) : undefined;
    if (editLessonCurrId === "__special__" && editForm.topic) {
      topic = `[Special Lesson] ${editForm.topic}`;
      lessonNum = 0;
    }
    await updateSession(editLesson.id, { date: editForm.date || undefined, topic, lesson_number: lessonNum });
    setEditLesson(null);
    loadGrid();
  };

  // --- Assignments ---
  const handleCreateAssignment = async () => {
    if (!id || !assignForm.title) return;
    const questions = assignQuestions.filter((q) => q.question_text.trim());
    await createAssignment({ class_id: id, ...assignForm, questions } as any);
    setAssignForm({ title: "", description: "", due_date: "" });
    setAssignQuestions([{ question_text: "", answer_text: "" }]);
    setShowAddAssignment(false);
    loadAssignments();
  };

  const handleDeleteAssignment = async (assignmentId: string) => {
    if (!window.confirm("Delete this assignment?")) return;
    await deleteAssignment(assignmentId);
    loadAssignments();
  };

  const handleSubmit = async () => {
    if (!showSubmitModal || !submitForm.student_id) return;
    const fd = new FormData();
    fd.append("assignment_id", showSubmitModal);
    fd.append("student_id", submitForm.student_id);
    fd.append("source", "teacher");
    if (submitForm.text_content) fd.append("text_content", submitForm.text_content);
    if (submitForm.files) {
      for (let i = 0; i < submitForm.files.length; i++) {
        fd.append("files", submitForm.files[i]);
      }
    }
    await createSubmission(fd);
    setShowSubmitModal(null);
    setSubmitForm({ student_id: "", text_content: "", files: null });
    loadAssignments();
  };

  const submissionCount = (assignmentId: string) =>
    submissions.filter((s) => s.assignment_id === assignmentId).length;

  const monthGroups = grid ? groupByMonth(grid.columns) : [];

  if (notFound) return (
    <div className="empty">
      Class not found. <Link to="/classes" style={{ color: "var(--accent)" }}>Go to Classes</Link>
    </div>
  );
  if (!cls) return <div className="empty">Loading...</div>;

  return (
    <div>
      <div className="detail-header">
        <div>
          <Link to="/classes" style={{ color: "var(--text-muted)", fontSize: 13 }}>&larr; Back to Classes</Link>
          <h2
            contentEditable
            suppressContentEditableWarning
            onBlur={async (e) => {
              const newName = e.currentTarget.textContent?.trim();
              if (newName && newName !== cls.name && id) {
                await updateClass(id, { name: newName });
                loadClass();
              }
            }}
            style={{ cursor: "text", outline: "none", borderBottom: "1px dashed transparent" }}
            onFocus={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--accent)"; }}
            onMouseOver={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--border)"; }}
            onMouseOut={(e) => { if (document.activeElement !== e.currentTarget) e.currentTarget.style.borderBottom = "1px dashed transparent"; }}
          >{cls.name}</h2>
          <div style={{ color: "var(--text-muted)", fontSize: 14, display: "flex", alignItems: "center", gap: 12 }}>
            <span>
              {cls.schedule && (
                <span
                  contentEditable
                  suppressContentEditableWarning
                  style={{ cursor: "text", outline: "none", borderBottom: "1px dashed transparent" }}
                  onFocus={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--accent)"; }}
                  onBlur={async (e) => {
                    e.currentTarget.style.borderBottom = "1px dashed transparent";
                    const val = e.currentTarget.textContent?.trim();
                    if (val && val !== cls.schedule && id) {
                      await updateClass(id, { schedule: val });
                      loadClass();
                    }
                  }}
                >{cls.schedule}</span>
              )}
              {cls.schedule && <span> &middot; </span>}
              {cls.student_count} students
            </span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                const url = `${window.location.origin}/hw/${id}`;
                navigator.clipboard.writeText(url);
                alert("Homework link copied!\n" + url);
              }}
            >Copy Homework Link</button>
          </div>
        </div>
      </div>

      <div className="tab-bar">
        <button className={tab === "students" ? "active" : ""} onClick={() => setTab("students")}>
          Students ({cls.students?.length || 0})
        </button>
        <button className={tab === "attendance" ? "active" : ""} onClick={() => setTab("attendance")}>
          Attendance ({grid?.columns.length || 0} lessons)
        </button>
        <button className={tab === "assignments" ? "active" : ""} onClick={() => setTab("assignments")}>
          Assignments ({assignments.length})
        </button>
        <button className={tab === "settings" ? "active" : ""} onClick={() => setTab("settings")}>
          Settings
        </button>
      </div>

      {/* ===== STUDENTS TAB ===== */}
      {tab === "students" && (
        <>
          <div className="toolbar">
            <span></span>
            <button className="btn btn-primary" onClick={() => setShowAddStudent(true)}>+ Add Student</button>
          </div>
          <div className="table-container">
            {(!cls.students || cls.students.length === 0) ? (
              <div className="empty">No students enrolled. Add students to start tracking attendance.</div>
            ) : (
              <table>
                <thead><tr><th>#</th><th>Name</th><th>TG Topic</th><th></th></tr></thead>
                <tbody>
                  {cls.students.map((s: any, i: number) => (
                    <tr key={s.id}>
                      <td style={{ color: "var(--text-muted)", width: 40 }}>{i + 1}</td>
                      <td>
                        <span
                          contentEditable
                          suppressContentEditableWarning
                          style={{ color: "var(--accent)", fontWeight: 500, cursor: "text", outline: "none", borderBottom: "1px dashed transparent", padding: "2px 4px", borderRadius: 4 }}
                          onFocus={(e) => { e.currentTarget.style.borderBottom = "1px dashed var(--accent)"; e.currentTarget.style.background = "var(--accent-light)"; }}
                          onBlur={async (e) => {
                            e.currentTarget.style.borderBottom = "1px dashed transparent";
                            e.currentTarget.style.background = "transparent";
                            const newName = e.currentTarget.textContent?.trim();
                            if (newName && newName !== s.name) {
                              await updateStudent(s.id, { name: newName });
                              loadClass();
                            }
                          }}
                        >{s.name}</span>
                      </td>
                      <td style={{ fontSize: 12, color: s.telegram_topic_id ? "var(--green-text)" : "var(--text-muted)" }}>
                        {s.telegram_topic_id || "Not linked"}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <button className="btn btn-danger btn-sm" onClick={() => handleRemoveStudent(s.id, s.name)}>Remove</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showAddStudent && !showCreateStudent && (
            <div className="modal-overlay" onClick={() => setShowAddStudent(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Student to Class</h3>
                {availableStudents.length > 0 && (
                  <>
                    <div className="form-group">
                      <label>Existing Student</label>
                      <select value={selectedStudentId} onChange={(e) => setSelectedStudentId(e.target.value)}>
                        <option value="">Select...</option>
                        {availableStudents.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-actions" style={{ marginBottom: 0 }}>
                      <button className="btn btn-secondary" onClick={() => setShowAddStudent(false)}>Cancel</button>
                      <button className="btn btn-primary" onClick={handleAddStudent}>Add</button>
                    </div>
                    <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: 13, margin: "16px 0 8px", borderTop: "1px solid var(--border-light)", paddingTop: 16 }}>or</div>
                  </>
                )}
                <button
                  className="btn btn-secondary"
                  style={{ width: "100%" }}
                  onClick={() => setShowCreateStudent(true)}
                >
                  Create New Student
                </button>
              </div>
            </div>
          )}

          {showCreateStudent && (
            <div className="modal-overlay" onClick={() => { setShowCreateStudent(false); setShowAddStudent(false); }}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Create & Enroll Student</h3>
                <div className="form-group">
                  <label>Name *</label>
                  <input value={newStudentForm.name} onChange={(e) => setNewStudentForm({ ...newStudentForm, name: e.target.value })} />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone</label>
                    <input value={newStudentForm.phone} onChange={(e) => setNewStudentForm({ ...newStudentForm, phone: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>Email</label>
                    <input value={newStudentForm.email} onChange={(e) => setNewStudentForm({ ...newStudentForm, email: e.target.value })} />
                  </div>
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowCreateStudent(false)}>Back</button>
                  <button className="btn btn-primary" onClick={handleCreateAndAddStudent}>Create & Add</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== ATTENDANCE TAB ===== */}
      {tab === "attendance" && (
        <>
          <div style={{ marginBottom: 12 }}>
            <button className="btn btn-primary btn-sm" onClick={() => setShowAddLesson(true)}>+ Add Lesson</button>
            <div style={{ display: "flex", gap: 12, fontSize: 11, marginTop: 10 }}>
              {[
                { code: "IP", label: "In Person", bg: "var(--green-light)", color: "var(--green-text)" },
                { code: "CU", label: "Catch Up", bg: "var(--amber-light)", color: "var(--amber-text)" },
                { code: "X", label: "Absent", bg: "var(--red-light)", color: "var(--red-text)" },
              ].map((s) => (
                <div key={s.code} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <span style={{
                    display: "inline-block", width: 24, height: 18, borderRadius: 3,
                    background: s.bg, color: s.color, textAlign: "center", lineHeight: "18px",
                    fontWeight: 600, fontSize: 10,
                  }}>{s.code}</span>
                  <span style={{ color: "var(--text-secondary)" }}>{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {(!grid || grid.students.length === 0) ? (
            <div className="empty">Add students to this class first to start recording attendance.</div>
          ) : grid.columns.length === 0 ? (
            <div className="empty">No lessons recorded yet. Click "+ Add Lesson" to create the first column.</div>
          ) : (
            <>
              <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: "var(--radius)" }}>
                <table style={{ width: "auto", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    {/* Month row */}
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", padding: "4px 8px", width: 30, textAlign: "center", borderRight: "1px solid var(--border)" }}>#</th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", padding: "4px 10px", borderRight: "2px solid var(--border)" }}>Name</th>
                      {monthGroups.map((mg) => (
                        <th
                          key={mg.month}
                          colSpan={mg.cols.length}
                          style={{
                            textAlign: "center", background: "var(--accent)", color: "#fff",
                            fontWeight: 600, fontSize: 11, padding: "4px 0",
                            borderRight: "1px solid rgba(255,255,255,0.3)",
                          }}
                        >{mg.month}</th>
                      ))}
                    </tr>
                    {/* Day + date number row */}
                    <tr>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", borderRight: "1px solid var(--border)" }}></th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", borderRight: "2px solid var(--border)" }}></th>
                      {grid.columns.map((col) => (
                        <th
                          key={col.id}
                          style={{
                            textAlign: "center", padding: "3px 0", minWidth: 28, width: 28,
                            fontSize: 10, fontWeight: 600, background: "#f5f5f4",
                            borderRight: "1px solid var(--border-light)",
                          }}
                          title={`${col.day} ${col.day_num} — ${col.topic || col.date}`}
                        >
                          {col.day_num}
                        </th>
                      ))}
                    </tr>
                    {/* Lesson title row (vertical text like Excel) */}
                    <tr style={{ borderBottom: "2px solid var(--border)" }}>
                      <th style={{ position: "sticky", left: 0, zIndex: 3, background: "#f5f5f4", borderRight: "1px solid var(--border)" }}></th>
                      <th style={{ position: "sticky", left: 30, zIndex: 3, background: "#f5f5f4", padding: "2px 10px", fontSize: 9, color: "var(--text-muted)", borderRight: "2px solid var(--border)", verticalAlign: "bottom" }}>Topic</th>
                      {grid.columns.map((col) => (
                        <th
                          key={col.id}
                          style={{
                            background: "#f5f5f4",
                            borderRight: "1px solid var(--border-light)",
                            padding: 0,
                            height: col.topic ? 120 : 24,
                            verticalAlign: "bottom",
                            cursor: "pointer",
                            position: "relative",
                          }}
                          title={`${col.topic || "No topic"} (${col.date}) — click to edit`}
                          onClick={() => {
                            setEditLesson(col);
                            setEditForm({ date: col.date, topic: cleanTitle(col.topic || ""), lesson_number: col.lesson_number?.toString() || "" });
                            // Match to curriculum item
                            const match = curriculum.find((c) => c.lesson_number === col.lesson_number);
                            setEditLessonCurrId(col.topic?.startsWith("[Special Lesson]") ? "__special__" : match?.id || "");
                          }}
                        >
                          <div style={{
                            writingMode: "vertical-rl",
                            transform: "rotate(180deg)",
                            fontSize: 8,
                            fontWeight: 400,
                            color: col.topic ? "var(--text-secondary)" : "var(--text-muted)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            maxHeight: 115,
                            padding: "2px 0",
                            margin: "0 auto",
                            textAlign: "left",
                          }}>
                            {col.topic
                              ? (col.lesson_number ? `[L${col.lesson_number}] ${col.topic}` : col.topic)
                              : (col.lesson_number ? `L${col.lesson_number}` : "·")}
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.students.map((student, idx) => (
                      <tr key={student.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                        <td style={{
                          position: "sticky", left: 0, zIndex: 2, background: "#fff",
                          fontWeight: 500, fontSize: 11, padding: "0 8px", color: "var(--text-muted)",
                          textAlign: "center", borderRight: "1px solid var(--border)",
                          height: 32,
                        }}>{idx + 1}</td>
                        <td style={{
                          position: "sticky", left: 30, zIndex: 2, background: "#fff",
                          fontWeight: 500, fontSize: 12, padding: "0 10px", whiteSpace: "nowrap",
                          borderRight: "2px solid var(--border)",
                          height: 32,
                        }}>{student.name}</td>
                        {grid.columns.map((col) => {
                          const status = grid.grid[student.id]?.[col.id] || "";
                          const colors = STATUS_COLORS[status] || STATUS_COLORS[""];
                          return (
                            <td
                              key={col.id}
                              onClick={() => handleCellClick(col.id, student.id, status)}
                              style={{
                                textAlign: "center", padding: 0, cursor: "pointer",
                                borderRight: "1px solid var(--border-light)",
                                width: 28, height: 28, userSelect: "none",
                                background: status ? colors.bg : "transparent",
                              }}
                            >
                              <span style={{
                                fontWeight: 700, fontSize: 10,
                                color: status ? colors.color : "var(--border)",
                                lineHeight: "28px",
                              }}>{status || "\u00B7"}</span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={{ marginTop: 10, fontSize: 11, color: "var(--text-muted)", display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
                <span>Click cell to cycle: IP → CU → X → clear &middot; {grid.columns.length} lessons</span>
              </div>
            </>
          )}

          {showAddLesson && (
            <div className="modal-overlay" onClick={() => setShowAddLesson(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Add Lesson</h3>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Curriculum Lesson</label>
                  <select
                    value={addLessonCurrId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setAddLessonCurrId(val);
                      if (val === "__special__") {
                        setNewLessonNum("0");
                        setNewTopic("");
                      } else {
                        const item = curriculum.find((c) => c.id === val);
                        if (item) {
                          setNewLessonNum(item.lesson_number.toString());
                          setNewTopic(cleanTitle(item.title));
                        }
                      }
                    }}
                  >
                    <option value="">Select official lesson...</option>
                    {(() => {
                      const intro = curriculum.filter((c) => c.title.startsWith("[Intro]"));
                      const inter = curriculum.filter((c) => c.title.startsWith("[Intermediate]"));
                      const adv = curriculum.filter((c) => c.title.startsWith("[Advanced]"));
                      return (
                        <>
                          {intro.length > 0 && <optgroup label="Introductory">{intro.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {inter.length > 0 && <optgroup label="Intermediate">{inter.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {adv.length > 0 && <optgroup label="Advanced / Revelation">{adv.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                        </>
                      );
                    })()}
                    <option value="__special__">Special Lesson (not in official list)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lesson Title</label>
                  <input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} placeholder="e.g. 1-1 Consultation, Recap of Figurative Fire" />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowAddLesson(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleAddLesson}>Add</button>
                </div>
              </div>
            </div>
          )}

          {/* Generate Lessons Modal */}
          {showGenerate && (
            <div className="modal-overlay" onClick={() => setShowGenerate(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Generate Lessons from Schedule</h3>
                <p style={{ fontSize: 13, color: "var(--text-secondary)", marginBottom: 16 }}>
                  Schedule: <strong>{cls.schedule || "Not set"}</strong>
                  {!cls.schedule && <span style={{ color: "var(--red)" }}> — edit the class to set a schedule first</span>}
                </p>
                <div className="form-row">
                  <div className="form-group">
                    <label>Start Date *</label>
                    <input type="date" value={genStart} onChange={(e) => setGenStart(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>End Date *</label>
                    <input type="date" value={genEnd} onChange={(e) => setGenEnd(e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Start Lesson # from</label>
                  <input type="number" value={genStartNum} onChange={(e) => setGenStartNum(e.target.value)} />
                </div>
                {genResult && (
                  <div style={{ padding: 12, borderRadius: 8, background: genResult.startsWith("Created") ? "var(--green-light)" : "var(--red-light)", fontSize: 13, marginBottom: 12 }}>
                    {genResult}
                  </div>
                )}
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowGenerate(false)}>Close</button>
                  <button className="btn btn-primary" onClick={handleGenerate} disabled={!cls.schedule}>Generate</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Lesson Modal */}
          {editLesson && (
            <div className="modal-overlay" onClick={() => setEditLesson(null)}>
              <div className="modal" onClick={(e) => e.stopPropagation()}>
                <h3>Edit Lesson</h3>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Curriculum Lesson</label>
                  <select
                    value={editLessonCurrId}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditLessonCurrId(val);
                      if (val === "__special__") {
                        setEditForm({ ...editForm, lesson_number: "0", topic: "" });
                      } else {
                        const item = curriculum.find((c) => c.id === val);
                        if (item) {
                          setEditForm({ ...editForm, lesson_number: item.lesson_number.toString(), topic: cleanTitle(item.title) });
                        }
                      }
                    }}
                  >
                    <option value="">Select official lesson...</option>
                    {(() => {
                      const intro = curriculum.filter((c) => c.title.startsWith("[Intro]"));
                      const inter = curriculum.filter((c) => c.title.startsWith("[Intermediate]"));
                      const adv = curriculum.filter((c) => c.title.startsWith("[Advanced]"));
                      return (
                        <>
                          {intro.length > 0 && <optgroup label="Introductory">{intro.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {inter.length > 0 && <optgroup label="Intermediate">{inter.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                          {adv.length > 0 && <optgroup label="Advanced / Revelation">{adv.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}</optgroup>}
                        </>
                      );
                    })()}
                    <option value="__special__">Special Lesson (not in official list)</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Lesson Title</label>
                  <input value={editForm.topic} onChange={(e) => setEditForm({ ...editForm, topic: e.target.value })} placeholder="e.g. 1-1 Consultation" />
                </div>
                <div className="modal-actions">
                  <button className="btn btn-danger" onClick={() => { handleDeleteLesson(editLesson.id, editLesson.date); setEditLesson(null); }}>Delete</button>
                  <div style={{ flex: 1 }} />
                  <button className="btn btn-secondary" onClick={() => setEditLesson(null)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleEditLesson}>Save</button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== ASSIGNMENTS TAB ===== */}
      {tab === "assignments" && (
        <>
          <div className="toolbar">
            <span></span>
            <button className="btn btn-primary" onClick={() => setShowAddAssignment(true)}>+ New Assignment</button>
          </div>

          <div className="table-container">
            {assignments.length === 0 ? (
              <div className="empty">No assignments yet. Create one to start tracking homework.</div>
            ) : (
              <table>
                <thead>
                  <tr><th>Title</th><th>Due Date</th><th>Answers</th><th></th></tr>
                </thead>
                <tbody>
                  {assignments.map((a: any) => (
                    <tr key={a.id}>
                      <td>
                        <span style={{ fontWeight: 500, cursor: "pointer", color: "var(--accent)" }} onClick={() => setViewAssignment(a)}>
                          {a.title}
                        </span>
                        {a.questions?.length > 0 && (
                          <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>
                            {a.questions.length} Q&A
                          </span>
                        )}
                      </td>
                      <td>{a.due_date || "-"}</td>
                      <td style={{ cursor: "pointer" }} onClick={() => setViewAssignment(a)}>
                        <span className={`badge ${(a.answered_count || 0) > 0 ? "present" : "pending"}`}>
                          {a.answered_count || 0} / {cls.students?.length || 0}
                        </span>
                        <span style={{ fontSize: 11, color: "var(--accent)", marginLeft: 8 }}>View</span>
                      </td>
                      <td style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAssignment(a.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {showAddAssignment && (
            <div className="modal-overlay" onClick={() => setShowAddAssignment(false)}>
              <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 600 }}>
                <h3>New Assignment</h3>
                <div className="form-group">
                  <label>Title *</label>
                  <input value={assignForm.title} onChange={(e) => setAssignForm({ ...assignForm, title: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea value={assignForm.description} onChange={(e) => setAssignForm({ ...assignForm, description: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={assignForm.due_date} onChange={(e) => setAssignForm({ ...assignForm, due_date: e.target.value })} />
                </div>

                {/* Questions */}
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid var(--border-light)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Questions & Answers</label>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setAssignQuestions([...assignQuestions, { question_text: "", answer_text: "" }])}
                    >+ Add Question</button>
                  </div>
                  {assignQuestions.map((q, i) => (
                    <div key={i} style={{ background: "var(--bg)", borderRadius: 8, padding: 12, marginBottom: 8, position: "relative" }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>Q{i + 1}</div>
                      <div className="form-group" style={{ marginBottom: 8 }}>
                        <input
                          placeholder="Question"
                          value={q.question_text}
                          onChange={(e) => {
                            const updated = [...assignQuestions];
                            updated[i] = { ...updated[i], question_text: e.target.value };
                            setAssignQuestions(updated);
                          }}
                        />
                      </div>
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <input
                          placeholder="Answer (optional)"
                          value={q.answer_text}
                          onChange={(e) => {
                            const updated = [...assignQuestions];
                            updated[i] = { ...updated[i], answer_text: e.target.value };
                            setAssignQuestions(updated);
                          }}
                        />
                      </div>
                      {assignQuestions.length > 1 && (
                        <button
                          style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", color: "var(--red)", cursor: "pointer", fontSize: 16 }}
                          onClick={() => setAssignQuestions(assignQuestions.filter((_, j) => j !== i))}
                        >&times;</button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={() => setShowAddAssignment(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={handleCreateAssignment}>Create</button>
                </div>
              </div>
            </div>
          )}

          {/* View Assignment Modal with Student Answers */}
          {viewAssignment && (
            <ViewAssignmentModal
              assignment={viewAssignment}
              classId={id!}
              students={cls.students || []}
              onClose={() => setViewAssignment(null)}
            />
          )}

        </>
      )}

      {/* ===== SETTINGS TAB ===== */}
      {tab === "settings" && (
        <div style={{ maxWidth: 500 }}>
          <h3 style={{ fontFamily: "'Fraunces', serif", fontSize: 18, marginBottom: 16 }}>Telegram Notifications</h3>
          <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: 20 }}>
            1. Add the bot to your Telegram group (with forum topics enabled)<br />
            2. Make sure each student has a forum topic named after them<br />
            3. Enter the Chat ID below and click "Scan Topics"
          </p>

          <div className="form-group">
            <label>Telegram Chat ID</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={telegramForm.chat_id}
                onChange={(e) => setTelegramForm({ ...telegramForm, chat_id: e.target.value })}
                placeholder="e.g. -1001234567890"
                style={{ flex: 1 }}
              />
              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (!id || !telegramForm.chat_id) return;
                  await updateClass(id, { telegram_chat_id: telegramForm.chat_id });
                  try {
                    const r = await scanTopics(id);
                    const data = r.data as any;
                    setTelegramSaved(true);
                    setTimeout(() => setTelegramSaved(false), 5000);
                    loadClass();
                    alert(
                      `Scan complete!\n\nMatched: ${data.matched.length} students\n` +
                      (data.unmatched_students.length > 0 ? `Unmatched: ${data.unmatched_students.join(", ")}\n` : "") +
                      `Total topics found: ${data.total_topics_found}` +
                      (data.hint ? `\n\n${data.hint}` : "")
                    );
                  } catch (e: any) {
                    alert("Scan failed: " + (e.response?.data?.detail || e.message));
                  }
                }}
              >Scan Topics</button>
            </div>
          </div>

          {telegramSaved && (
            <div style={{ background: "var(--green-light)", color: "var(--green-text)", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 }}>
              Topics scanned and linked successfully!
            </div>
          )}

          {/* Show current status */}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: "var(--text-secondary)" }}>Current Status</h4>
            <div style={{ fontSize: 13, lineHeight: 2 }}>
              <div>Chat ID: <strong>{cls.telegram_chat_id || "Not set"}</strong></div>
              <div>Reminders Topic: <strong>{cls.telegram_reminder_topic_id || "Not found"}</strong></div>
            </div>

            <h4 style={{ fontSize: 14, fontWeight: 600, marginTop: 16, marginBottom: 10, color: "var(--text-secondary)" }}>Student Topics</h4>
            <div className="table-container">
              <table>
                <thead><tr><th>Student</th><th>Topic ID</th></tr></thead>
                <tbody>
                  {cls.students?.map((s: any) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td>
                        {s.telegram_topic_id ? (
                          <span style={{ color: "var(--green-text)" }}>{s.telegram_topic_id}</span>
                        ) : (
                          <span style={{ color: "var(--red)", fontStyle: "italic" }}>Not linked</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
